/**
 * Utility functions.
 */

import { t } from '@/i18n';
import { useLanguageStore, type Lang } from '@/store/languageStore';

const currentLang = (): Lang => useLanguageStore.getState().lang;

/** Locale used for date/number formatting. */
export const localeFor = (lang: Lang = currentLang()): string => (lang === 'en' ? 'en-US' : 'zh-CN');

/**
 * Format file size in human-readable format.
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Copy text to clipboard, falling back to a hidden-textarea + execCommand
 * when navigator.clipboard is unavailable (e.g. non-HTTPS test environments,
 * where the Clipboard API is only exposed in secure contexts).
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  if (window.isSecureContext && navigator.clipboard) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!ok) throw new Error('Copy to clipboard failed');
};

/**
 * Format upload/download speed in human-readable format.
 */
export const formatSpeed = (bytesPerSecond: number): string => {
  if (bytesPerSecond === 0) return '0 B/s';

  const k = 1024;
  const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));

  return `${parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format date for the current UI language.
 */
export const formatDate = (dateString: string, lang: Lang = currentLang()): string => {
  return new Date(dateString).toLocaleString(localeFor(lang), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Truncate text with ellipsis.
 */
export const truncate = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

/**
 * Get file extension from filename.
 */
export const getFileExtension = (filename: string): string => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
};

/**
 * Generate a unique ID.
 */
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Debounce function.
 */
export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => func(...args), wait);
  };
};

/**
 * Calculate file MD5 value (supports incremental calculation for large files).
 * @param file File to calculate MD5 for
 * @param chunkSize Chunk size for reading (default 2MB)
 * @returns Promise<string> MD5 value (32-character lowercase hex)
 */
import SparkMD5 from 'spark-md5';

export const calculateFileMD5 = async (
  file: File,
  chunkSize: number = 2 * 1024 * 1024
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();
    let currentChunk = 0;
    const chunks = Math.ceil(file.size / chunkSize);

    fileReader.onload = (e) => {
      if (e.target?.result) {
        spark.append(e.target.result as ArrayBuffer);
        currentChunk++;

        if (currentChunk < chunks) {
          loadNext();
        } else {
          resolve(spark.end());
        }
      }
    };

    fileReader.onerror = () => {
      reject(new Error('MD5 calculation failed'));
    };

    const loadNext = () => {
      const start = currentChunk * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      fileReader.readAsArrayBuffer(file.slice(start, end));
    };

    loadNext();
  });
};

/**
 * Format MD5 for display (lowercase hexadecimal).
 * @param md5 Complete MD5 value
 * @returns Formatted MD5 string
 */
export const formatMD5 = (md5: string): string => {
  if (!md5) return '';
  return md5.toLowerCase();
};

/**
 * Convert MD5 hex string to Base64 format (for OBS Content-MD5 header).
 * @param md5Hex MD5 hex string (32 characters)
 * @returns Base64-encoded MD5 value
 */
export const md5HexToBase64 = (md5Hex: string): string => {
  if (!md5Hex || md5Hex.length !== 32) return '';

  // Convert hex string to byte array
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(md5Hex.substr(i * 2, 2), 16);
  }

  // Convert to Base64
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * 格式化上传模式为中文显示
 */
export const formatUploadMode = (mode: string | null | undefined): string => {
  if (!mode) return '-';

  const modeMap: Record<string, string> = {
    'auto': t('自动选择'),
    'direct': t('直连上传'),
    'proxy': t('代理上传'),
  };

  return modeMap[mode] || mode;
};

/**
 * 格式化分片大小为可读格式
 */
export const formatChunkSize = (size: number | null | undefined): string => {
  if (size === null || size === undefined) return '-';
  if (size === 0) return t('自动选择');

  // 复用现有的 formatFileSize 函数
  return formatFileSize(size);
};
